import { useCallback } from 'react';
import { XOR } from 'ts-essentials';

import { ReimbursementIndication } from '@dreryk/egabinet-prescription-frontend-api';

import {
   appendTestableComponentAttributes,
   appendTestableCustomAttribute,
   Tooltip,
   TooltipTrigger,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { OnReimbursementClick, ReimbursementVariant } from './Reimbursement.types';
import { ReimbursementTooltipContent } from './ReimbursementTooltipContent';
import {
   StyledReimbursement,
   StyledReimbursementCode,
   StyledReimbursementIcon,
   StyledReimbursementPriceDescription,
} from './Reimbursement.styled';

type ReimbursementInteractionProps = {
   onClick?: OnReimbursementClick;
   icon?: JSX.Element;
};

type ReimbursementDisabledProps = {
   disabled: true;
};

type ReimbursementProps = {
   readonly code: string;
   readonly priceDescription: string;
   readonly tooltipContent?: ReimbursementTooltipContent[] | string[] | string;
   readonly variant: ReimbursementVariant;
   readonly active?: boolean;
   readonly reimbursementIndications?: ReimbursementIndication[];
   readonly distinct?: boolean;
} & XOR<ReimbursementInteractionProps, ReimbursementDisabledProps>;

export const Reimbursement = (props: ReimbursementProps): JSX.Element => {
   const extendedMedicineDatabaseAvailable = protectFeature('extendedMedicineDatabase');
   const handleClick = useCallback(() => {
      props.onClick?.({
         code: props.code,
         priceDescription: props.priceDescription,
         reimbursementIndication: props.reimbursementIndications,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.code, props.priceDescription, props.onClick]);

   const distinct = props.distinct && extendedMedicineDatabaseAvailable;

   return (
      <Tooltip
         testId={`reimbursement-${props.code}`}
         trigger={TooltipTrigger.HOVER}
         maxWidth={'700px'}
         disabled={props.tooltipContent ? props.tooltipContent.length === 0 : true}
         content={props.tooltipContent ? <ReimbursementTooltipContent tooltipContent={props.tooltipContent} /> : null}
      >
         <StyledReimbursement
            $variant={props.variant}
            onClick={handleClick}
            $clickable={Boolean(props.onClick)}
            $active={props.active}
            disabled={props.disabled}
            $distinct={distinct}
            $icon={Boolean(props.icon)}
            {...appendTestableComponentAttributes('Reimbursement', props.code)}
            {...appendTestableCustomAttribute('checked', props.active)}
         >
            <StyledReimbursementCode $clickable={Boolean(props.onClick)}>{props.code}</StyledReimbursementCode>
            <StyledReimbursementPriceDescription>{props.priceDescription}</StyledReimbursementPriceDescription>
            {props.icon ? <StyledReimbursementIcon>{props.icon}</StyledReimbursementIcon> : null}
         </StyledReimbursement>
      </Tooltip>
   );
};
